import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    InputSignal,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {StoreData, StoreDisplayStatus} from "../store-search.model";
import {OrderType} from '../../order/order';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ToggleComponent} from "../../ui-elements/toggle/toggle.component";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {WOATimePipe} from '../../utils/time.pipe';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

export type DayOfWeek =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';

@Component({
    selector: "woa-store-status-panel",
    templateUrl: "./store-status-panel.component.html",
    styleUrl: "./store-status-panel.component.scss",
    imports: [
        NgClass,
        ToggleComponent,
        TouchableDirective,
        TranslateModule,
        WOATimePipe,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreStatusPanelComponent {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);

    public readonly store: InputSignal<StoreData> = input.required<StoreData>();
    public readonly filteredOrderType: InputSignal<OrderType> = input.required<OrderType>();

    public showHours: WritableSignal<boolean> = signal<boolean>(false);

    daysOfWeek: DayOfWeek[] = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
    ];

    public readonly displayStatus: Signal<StoreDisplayStatus> = computed(() =>
        this.store() ? this.store().getDisplayStatus(this.filteredOrderType() || this.orderingContextModel.orderType()) : undefined);

    public readonly isOpen: Signal<boolean> = computed(() => {
        const displayStatus: StoreDisplayStatus = this.displayStatus();
        return displayStatus === StoreDisplayStatus.CURRENTLY_OPEN || displayStatus === StoreDisplayStatus.OPEN_AT_TIME;
    });

    public readonly statusLabel: Signal<string> = computed(() => {
        switch (this.displayStatus()) {
            case StoreDisplayStatus.OFFLINE:
                return "woa.label.store-status.offline";
            case StoreDisplayStatus.CURRENTLY_CLOSED:
                return "woa.label.store-status.currentlyClosed";
            case StoreDisplayStatus.CLOSED_AT_TIME:
                return "woa.label.store-status.closedAtTime";
            case StoreDisplayStatus.CURRENTLY_OPEN:
                return "woa.label.store-status.currentlyOpen";
            case StoreDisplayStatus.OPEN_AT_TIME:
                return "woa.label.store-status.openAtTime";
        }
    });

    toggleHours(hours: HTMLElement): void {
        this.showHours.set(!this.showHours());
        hours.style.maxHeight = this.showHours() ? hours.scrollHeight + "px" : "0";
    }
}
